import { useState } from 'react';

import type { Payment } from '@models/payments';
import {
  BRAND_CODE_OPTIONS,
  CAPTURE_STATE_OPTIONS,
  PAYMENT_SEARCH_BY_OPTIONS,
  PAYMENT_STATUS_OPTIONS,
  PAYMENT_TYPE_OPTIONS,
} from '@features/payments/constants';

import Pagination from '@components/Pagination';
import { useMediaQuery } from '@hooks/useMediaQuery';
import { Breakpoints } from '@utils/constants';

import PaymentsFilters from '../../components/Payments/PaymentsFilters';
import PaymentsTable from '../../components/Payments/PaymentsTable';
import PaymentsCardList from '../../components/Payments/PaymentsCardList';
import PaymentDetailDrawer from '../../components/Payments/PaymentDetailDrawer';
import { usePaymentsList } from '../../components/Payments/hooks/usePaymentsList';
import s from './PaymentsPage.module.css';

function PaymentsPage() {
  const list = usePaymentsList();
  const [selected, setSelected] = useState<Payment | null>(null);
  const isMobile = useMediaQuery({ query: `(max-width: ${Breakpoints.md})` });

  // The drawer renders the row it was opened with, so re-read it from the refetched page — otherwise
  // an action taken inside the drawer (capture) leaves it showing the pre-action snapshot.
  const selectedPayment = selected
    ? (list.rows.find((row) => row.id === selected.id) ?? selected)
    : null;

  return (
    <div>
      <div className={s.container}>
        <header className={s.header}>
          <div>
            <h1 className={s.title}>Payments</h1>
          </div>
        </header>

        <PaymentsFilters
          search={list.search}
          onSearchChange={list.setSearch}
          searchBy={list.searchBy}
          onSearchByChange={list.setSearchBy}
          searchByOptions={PAYMENT_SEARCH_BY_OPTIONS}
          searchError={list.searchError}
          status={list.status}
          onStatusChange={list.setStatus}
          type={list.type}
          onTypeChange={list.setType}
          brandCode={list.brandCode}
          onBrandCodeChange={list.setBrandCode}
          captureState={list.captureState}
          onCaptureStateChange={list.setCaptureState}
          fromDate={list.fromDate}
          onFromDateChange={list.setFromDate}
          toDate={list.toDate}
          onToDateChange={list.setToDate}
          statusOptions={PAYMENT_STATUS_OPTIONS}
          typeOptions={PAYMENT_TYPE_OPTIONS}
          brandCodeOptions={BRAND_CODE_OPTIONS}
          captureStateOptions={CAPTURE_STATE_OPTIONS}
          onClear={list.clearFilters}
        />

        {isMobile ? (
          <PaymentsCardList
            isLoading={list.isLoading}
            isFetching={list.isFetching}
            rows={list.rows}
            sort={list.sort}
            onSort={list.handleSort}
            onRowClick={setSelected}
          />
        ) : (
          <PaymentsTable
            isLoading={list.isLoading}
            isFetching={list.isFetching}
            rows={list.rows}
            sort={list.sort}
            onSort={list.handleSort}
            onRowClick={setSelected}
          />
        )}

        {!list.isLoading && (
          <Pagination
            page={list.page}
            setPage={list.setPage}
            rowsPerPage={list.rowsPerPage}
            setRowsPerPage={list.setRowsPerPage}
            total={list.total}
          />
        )}
      </div>

      <PaymentDetailDrawer
        payment={selectedPayment}
        statusFilter={list.status}
        onClose={() => setSelected(null)}
      />
    </div>
  );
}

export default PaymentsPage;
